/*
 * Copyright (c) Schrocken Inc.
 * Author: Schrocken Dev
 * File Description: IDoc Report view — displays the outbound-IDoc rows for the
 * selected date range, read from the xlsx transferred from the CMO host.
 * "What Changed" opens a modal that explains why each update IDoc was sent.
 */

import React, { Component } from 'react';
import axios from 'axios';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import GetAppIcon from '@material-ui/icons/GetApp';
import moment from 'moment-timezone';

import { appUrl } from '../../../../utils/config';

const ADMIN_API_BASE = `${appUrl}/Pharma%20Contract%20Manufacturing/N1/sh1/Pharma%20Co/Admin`;
const IDOC_REPORT_API_URL = `${ADMIN_API_BASE}/idocReport`;

const PAGE_SIZES = [10, 25, 50];

// Columns exported to the downloaded report — same order and headers as the
// source IDoc xlsx (see idoc_report.py / idocReport.js). "Created At (UTC)"
// keeps the raw UTC timestamp from the file so the export matches the source.
// Every value is written as a string so Excel preserves leading zeros in the
// Version column (e.g. "00017" instead of 17).
const EXPORT_COLUMNS = [
  { header: 'PO Number',            get: (r) => r.poNumber },
  { header: 'CMO Name',             get: (r) => r.cmoName },
  { header: 'Message Type',         get: (r) => r.messageType },
  { header: 'Change Type',          get: (r) => r.changeType },
  { header: 'Version (Mosymphony)', get: (r) => r.version },
  { header: 'Created At (UTC)',     get: (r) => r.createdAt },
  { header: 'What Changed',         get: (r) => r.whatChanged },
];

// Excel sheet names cap at 31 chars and disallow : \ / ? * [ ]. Date sheet
// names ("2026-09-15") are already safe, but sanitise defensively.
function safeSheetName(name) {
  return String(name).replace(/[:\\/?*[\]]/g, '-').slice(0, 31) || 'Sheet';
}

const styles = () => ({
  headerRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: '0.75rem',
    gap: '1rem',
  },
  downloadBtn: {
    backgroundColor: '#f26522',
    color: '#ffffff',
    textTransform: 'none',
    fontWeight: 600,
    padding: '0.35rem 1.1rem',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#d4541a',
    },
    '&:disabled': {
      backgroundColor: '#f5a882',
      color: '#ffffff',
    },
  },
  metaText: {
    fontSize: '0.8rem',
    color: '#777777',
  },
  tableContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
  },
  tableHead: {
    backgroundColor: '#fafafa',
  },
  headCell: {
    fontWeight: 700,
    fontSize: '0.8rem',
    color: '#333333',
    padding: '10px 14px',
    whiteSpace: 'nowrap',
  },
  bodyCell: {
    fontSize: '0.82rem',
    color: '#333333',
    padding: '12px 14px',
  },
  badge: {
    display: 'inline-block',
    padding: '2px 12px',
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  badgeUpdate: { backgroundColor: '#fdeeda', color: '#c47b1e' },
  badgeNew:    { backgroundColor: '#e2f3e6', color: '#2e7d43' },
  badgeDelete: { backgroundColor: '#fde3e3', color: '#c53434' },
  eyeBtn: {
    border: '1px solid #cccccc',
    borderRadius: 6,
    padding: 5,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.75rem',
    marginTop: '1.25rem',
    fontSize: '0.85rem',
    color: '#555555',
  },
  pageBtn: {
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    fontSize: '1rem',
    color: '#555555',
    padding: '4px 8px',
    '&:disabled': { color: '#cccccc', cursor: 'default' },
  },
  loaderWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '3rem',
  },
  errorText: {
    color: '#f44336',
    marginTop: '1rem',
  },
  // ── modal ──
  modalTitle: {
    fontWeight: 700,
    fontSize: '1.15rem',
    marginBottom: '0.25rem',
  },
  modalSubtitle: {
    fontSize: '0.85rem',
    color: '#666666',
    marginBottom: '1rem',
  },
  causeEffect: {
    display: 'flex',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    marginBottom: '1rem',
  },
  causeBox: {
    flex: 1,
    padding: '0.9rem 1rem',
    borderRight: '1px solid #e0e0e0',
  },
  ceLabel: {
    fontSize: '0.7rem',
    letterSpacing: 1,
    color: '#888888',
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  ceValue: {
    fontWeight: 700,
    fontSize: '0.95rem',
  },
  narrative: {
    backgroundColor: '#f7f7f7',
    borderLeft: '3px solid #333333',
    borderRadius: 6,
    padding: '0.9rem 1rem',
    fontSize: '0.9rem',
    marginBottom: '1rem',
    whiteSpace: 'pre-wrap',
  },
  metaBox: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: '0.9rem 1rem',
    marginBottom: '1.25rem',
    fontSize: '0.85rem',
  },
  sectionLabel: {
    fontSize: '0.75rem',
    letterSpacing: 1,
    color: '#888888',
    textTransform: 'uppercase',
    margin: '0.25rem 0 0.5rem',
    fontWeight: 700,
  },
  diffTable: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
  },
  modalFooter: {
    fontSize: '0.8rem',
    color: '#777777',
    marginTop: '0.75rem',
  },
});

// ---------------------------------------------------------------------------
// "What Changed" text parser — converts the plain-text diff produced by
// idoc_report.py into structured rows for the modal.
//
// Text forms:
//   "New PO (baseline)"
//   "PO/line delete"
//   "Baseline (first post-cutover IDoc)"
//   "First update after New PO (<ts>) - not diffed"
//   "No change vs <ts> (v00001) - re-send/duplicate"
//   "vs <ts> (v00001):\nL1: Qty 10->20; Delivery a->b\nL1 batch: ADM6976 (new) 981"
// ---------------------------------------------------------------------------
function parseWhatChanged(text) {
  const result = { kind: 'other', refCreated: '', refVersion: '', changes: [], raw: text || '' };
  if (!text) return result;

  const refM = /vs (\d{4}-\d{2}-\d{2}[ \d:]*)\s*\(v([^)]*)\)/.exec(text);
  if (refM) {
    result.refCreated = refM[1].trim();
    result.refVersion = refM[2];
  }

  if (text.startsWith('New PO'))        { result.kind = 'new';      return result; }
  if (text.startsWith('PO/line delete')){ result.kind = 'delete';   return result; }
  if (text.startsWith('Baseline'))      { result.kind = 'baseline'; return result; }
  if (text.startsWith('First update'))  { result.kind = 'notDiffed';return result; }
  if (text.startsWith('No change'))     { result.kind = 'noChange'; return result; }

  result.kind = 'diff';
  const lines = text.split('\n').slice(1);   // first line is the "vs ..." ref
  for (const line of lines) {
    const batchM = /^L(\S+) batch: (.+)$/.exec(line);
    if (batchM) {
      const lineNo = batchM[1];
      // "ADM6976 (new) 981" | "ADM6976 10->20" | "ADM6976 10->0 (removed)"
      const b = batchM[2];
      const newM = /^(\S+) \(new\) (.*)$/.exec(b);
      const remM = /^(\S+) (.+)->0 \(removed\)$/.exec(b);
      const chgM = /^(\S+) (.+)->(.+)$/.exec(b);
      if (newM) {
        result.changes.push({ line: lineNo, field: `Batch — ${newM[1]}`, before: 'Not allocated', after: `Qty ${newM[2]}`, tag: 'Batch allocated' });
      } else if (remM) {
        result.changes.push({ line: lineNo, field: `Batch — ${remM[1]}`, before: `Qty ${remM[2]}`, after: 'Removed', tag: 'Removed' });
      } else if (chgM) {
        result.changes.push({ line: lineNo, field: `Batch — ${chgM[1]}`, before: `Qty ${chgM[2]}`, after: `Qty ${chgM[3]}`, tag: 'Qty changed' });
      } else {
        result.changes.push({ line: lineNo, field: 'Batch', before: '', after: b, tag: 'Changed' });
      }
      continue;
    }
    const lineM = /^L(\S+): (.+)$/.exec(line);
    if (!lineM) continue;
    const lineNo = lineM[1];
    const body = lineM[2];
    if (body === 'item ADDED')   { result.changes.push({ line: lineNo, field: 'Line item', before: '—', after: 'Added',   tag: 'Added' });   continue; }
    if (body === 'item REMOVED') { result.changes.push({ line: lineNo, field: 'Line item', before: 'Present', after: 'Removed', tag: 'Removed' }); continue; }
    for (const part of body.split('; ')) {
      const pM = /^(Qty|Delivery|Stat date|Price|Material) (.*)->(.*)$/.exec(part);
      if (pM) {
        result.changes.push({
          line: lineNo,
          field: pM[1] === 'Qty' ? 'Quantity' : pM[1],
          before: pM[2],
          after: pM[3] || 'Cleared',
          tag: pM[3] ? 'Changed' : 'Removed',
        });
      }
    }
  }
  return result;
}

// The xlsx stores creation timestamps in UTC ("yyyy-mm-dd HH:mm:ss").
// Platform-wide convention is to show the user's local time — same approach
// as fe-pcmp's convertToLocaleDateTime (moment-timezone + tz.guess()), with
// the IDoc report's timestamp format added to the accepted-format list.
function toLocalTime(input) {
  try {
    if (!input) return '';

    const userTimeZone = moment.tz.guess();
    const userLocale = navigator?.language || 'en-US';

    if (input instanceof Date) {
      input = input.toISOString();
    }

    const formats = [
      'YYYY-MM-DD HH:mm:ss',      // IDoc report xlsx format ("2026-08-03 10:53:10")
      'DD-MMM-YYYY HH:mm:ss',     // "01-Mar-2025 07:24:22"
      'DD-MM-YYYY HH:mm:ss',      // "03-01-2025 17:25:51" (European)
      'MM-DD-YYYY HH:mm:ss',      // "03-01-2025 17:25:51" (US)
      'YYYY-MM-DDTHH:mm:ss.SSS',  // "2025-03-01T07:24:22.229"
      moment.ISO_8601,
    ];

    const date = moment.tz(input, formats, true, 'UTC').tz(userTimeZone);
    if (!date.isValid()) return input;   // show the raw value rather than an error

    return date.locale(userLocale).format('DD-MMM-YYYY HH:mm:ss');
  } catch {
    return input || '';
  }
}

// Human cause label from the parsed changes
function causeLabel(parsed) {
  if (parsed.kind === 'new')      return 'New PO created';
  if (parsed.kind === 'delete')   return 'PO / line deleted';
  if (parsed.kind === 'noChange') return 'Re-send (no field change)';
  if (parsed.kind === 'baseline' || parsed.kind === 'notDiffed') return 'First IDoc after cutover';
  const fields = parsed.changes.map((c) => c.field);
  if (fields.some((f) => f.startsWith('Batch'))) return 'New batch allocated';
  if (fields.includes('Delivery'))               return 'Delivery date changed';
  if (fields.includes('Quantity'))               return 'Quantity changed';
  if (fields.includes('Price'))                  return 'Price changed';
  return 'PO details updated';
}

class IdocReport extends Component {
  state = {
    loading: false,
    rows: null,
    summary: [],
    total: 0,
    fileName: '',
    generatedAt: '',
    error: null,
    page: 0,
    pageSize: 10,
    modalRow: null,
    downloading: false,
    downloadError: null,
  };

  componentDidMount() {
    this.fetchReport();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.startDate !== this.props.startDate || prevProps.endDate !== this.props.endDate) {
      this.fetchReport();
    }
  }

  fetchReport = async () => {
    const { startDate, endDate } = this.props;
    if (!startDate || !endDate) return;
    this.setState({ loading: true, error: null, downloadError: null, rows: null, page: 0 });
    try {
      const res = await axios.post(IDOC_REPORT_API_URL, { startDate, endDate });
      if (res.data.status) {
        this.setState({
          rows: res.data.rows,
          summary: res.data.summary || [],
          total: res.data.total,
          fileName: res.data.fileName,
          generatedAt: res.data.generatedAt,
        });
      } else {
        this.setState({ error: res.data.errorMessage || 'Failed to fetch IDoc report.' });
      }
    } catch (err) {
      this.setState({
        error:
          err.response?.data?.errorMessage ||
          err.message ||
          'An error occurred. Please try again.',
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  // Builds a multi-sheet .xlsx that mirrors the source IDoc file: a "Summary"
  // tab followed by one tab per date (newest first). SheetJS is loaded lazily
  // (dynamic import) so it stays out of the initial bundle and only loads when
  // the user actually clicks Download.
  handleDownload = async () => {
    const { rows, summary, total, downloading } = this.state;
    const { startDate, endDate } = this.props;
    if (downloading || !rows || rows.length === 0) return;

    this.setState({ downloading: true, downloadError: null });
    try {
      const mod = await import('xlsx');
      const XLSX = mod && mod.utils ? mod : (mod.default || mod);

      const wb = XLSX.utils.book_new();

      // Summary tab first — mirrors the source file's "Summary" sheet.
      const summaryAoa = [['Date', 'IDoc Count']];
      (summary || []).forEach((s) => summaryAoa.push([s.date, s.idocCount]));
      summaryAoa.push(['TOTAL', total || 0]);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryAoa), 'Summary');

      // Group the flattened rows back into per-date sheets (newest first).
      const header = EXPORT_COLUMNS.map((c) => c.header);
      const rowsByDate = new Map();
      rows.forEach((r) => {
        const key = r.date || 'Unknown';
        if (!rowsByDate.has(key)) rowsByDate.set(key, []);
        rowsByDate.get(key).push(r);
      });
      Array.from(rowsByDate.keys())
        .sort()
        .reverse()
        .forEach((d) => {
          const aoa = [header];
          rowsByDate.get(d).forEach((r) => {
            aoa.push(EXPORT_COLUMNS.map((c) => {
              const v = c.get(r);
              return v == null ? '' : String(v);   // strings keep leading zeros
            }));
          });
          XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), safeSheetName(d));
        });

      const range = startDate === endDate ? startDate : `${startDate}_to_${endDate}`;
      XLSX.writeFile(wb, `IDoc_Report_${range}.xlsx`);
    } catch (err) {
      this.setState({ downloadError: 'Failed to generate the report file. Please try again.' });
    } finally {
      this.setState({ downloading: false });
    }
  };

  badgeClass = (changeType) => {
    const { classes } = this.props;
    if (changeType === 'Update') return `${classes.badge} ${classes.badgeUpdate}`;
    if (changeType === 'Delete') return `${classes.badge} ${classes.badgeDelete}`;
    return `${classes.badge} ${classes.badgeNew}`;
  };

  renderModal() {
    const { classes } = this.props;
    const { modalRow } = this.state;
    if (!modalRow) return null;

    const parsed = parseWhatChanged(modalRow.whatChanged);
    const cause = causeLabel(parsed);
    const nChanges = parsed.changes.length;

    return (
      <Dialog open onClose={() => this.setState({ modalRow: null })} maxWidth='md' fullWidth>
        <DialogContent style={{ padding: '1.75rem 2rem' }}>
          <Typography className={classes.modalTitle}>Why this update was sent</Typography>
          <Typography className={classes.modalSubtitle}>
            {parsed.refVersion
              ? `PO change detected between Version ${parsed.refVersion} and Version ${modalRow.version || 'n/a'}`
              : `PO ${modalRow.poNumber} — ${modalRow.changeType}`}
          </Typography>

          {/* Cause → Effect */}
          <div className={classes.causeEffect}>
            <div className={classes.causeBox}>
              <div className={classes.ceLabel}>Cause</div>
              <div className={classes.ceValue}>{cause}</div>
            </div>
            <div className={classes.causeBox} style={{ borderRight: 'none' }}>
              <div className={classes.ceLabel}>Effect</div>
              <div className={classes.ceValue}>
                {modalRow.changeType === 'New' ? 'New PO IDoc generated' : `${modalRow.changeType} IDoc generated`}
              </div>
            </div>
          </div>

          {/* Narrative */}
          <div className={classes.narrative}>
            {parsed.kind === 'diff' && (
              <>PO <b>{modalRow.poNumber}</b> changed, so the system automatically generated an{' '}
                <b>update IDoc</b> (message type <b>{modalRow.messageType}</b>) to sync this PO with SAP.</>
            )}
            {parsed.kind === 'noChange' && (
              <>No field-level difference was found against the previous IDoc — this is a{' '}
                <b>re-send / duplicate</b> of the same PO data.</>
            )}
            {parsed.kind === 'new' && (
              <>A <b>new purchase order</b> was created and its first IDoc (message type{' '}
                <b>{modalRow.messageType}</b>) was sent to SAP as the baseline.</>
            )}
            {parsed.kind === 'delete' && (
              <>A <b>PO or line-item deletion</b> triggered this IDoc to sync the removal with SAP.</>
            )}
            {(parsed.kind === 'baseline' || parsed.kind === 'notDiffed') && (
              <>This is the <b>first IDoc after cutover</b> for this PO — there is no earlier
                version to compare against, so no field-level diff is available.</>
            )}
            {parsed.kind === 'other' && (parsed.raw || 'No change details available.')}
          </div>

          {/* Meta */}
          <div className={classes.metaBox}>
            <div className={classes.ceLabel}>Message Type</div>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>{modalRow.messageType}</div>
            {modalRow.version && (
              <>
                <div className={classes.ceLabel}>Updated To Version</div>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>v{modalRow.version}</div>
              </>
            )}
            {parsed.refCreated && (
              <>
                <div className={classes.ceLabel}>Compared Against</div>
                <div style={{ fontWeight: 700 }}>
                  Version {parsed.refVersion || 'n/a'} · Created {toLocalTime(parsed.refCreated)}
                </div>
              </>
            )}
          </div>

          {/* Field-level diff */}
          {nChanges > 0 && (
            <>
              <Typography className={classes.sectionLabel}>Field-Level Details</Typography>
              <TableContainer className={classes.diffTable}>
                <Table size='small'>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell className={classes.headCell}>Line</TableCell>
                      <TableCell className={classes.headCell}>Field</TableCell>
                      <TableCell className={classes.headCell}>Before</TableCell>
                      <TableCell className={classes.headCell}>After</TableCell>
                      <TableCell className={classes.headCell}>Change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsed.changes.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell className={classes.bodyCell}>L{c.line}</TableCell>
                        <TableCell className={classes.bodyCell}>{c.field}</TableCell>
                        <TableCell className={classes.bodyCell}><i>{c.before}</i></TableCell>
                        <TableCell className={classes.bodyCell}>{c.after}</TableCell>
                        <TableCell className={classes.bodyCell}>
                          <span className={`${classes.badge} ${
                            c.tag === 'Removed' ? classes.badgeDelete : classes.badgeUpdate}`}>
                            {c.tag}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography className={classes.modalFooter}>
                {nChanges} field change{nChanges > 1 ? 's' : ''} · 1 {modalRow.changeType.toLowerCase()} IDoc generated
              </Typography>
            </>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  render() {
    const { classes } = this.props;
    const { loading, rows, error, page, pageSize, fileName, generatedAt, downloading, downloadError } = this.state;

    if (loading) {
      return (
        <div className={classes.loaderWrapper}>
          <CircularProgress />
        </div>
      );
    }

    if (error) {
      return (
        <Typography variant='body2' className={classes.errorText}>
          {error}
        </Typography>
      );
    }

    if (!rows) return null;

    const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
    const pageRows = rows.slice(page * pageSize, (page + 1) * pageSize);

    return (
      <div>
        <div className={classes.headerRow}>
          {generatedAt && (
            <Typography className={classes.metaText}>
              Source: {fileName} · Last Synced @ {new Date(generatedAt).toLocaleString()}
            </Typography>
          )}
          {downloadError && (
            <Typography className={classes.errorText} style={{ margin: 0 }}>
              {downloadError}
            </Typography>
          )}
          <Button
            variant='contained'
            className={classes.downloadBtn}
            startIcon={downloading ? null : <GetAppIcon />}
            onClick={this.handleDownload}
            disabled={!rows || rows.length === 0 || downloading}
          >
            {downloading ? (
              <CircularProgress size={18} style={{ color: '#ffffff' }} />
            ) : (
              'Download'
            )}
          </Button>
          <Typography className={classes.metaText} style={{ fontWeight: 700, color: '#333' }}>
            PER PAGE&nbsp;
            <select
              value={pageSize}
              onChange={(e) => this.setState({ pageSize: parseInt(e.target.value, 10), page: 0 })}
            >
              {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Typography>
        </div>

        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table size='small'>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.headCell}>PO Number</TableCell>
                <TableCell className={classes.headCell}>CMO Name</TableCell>
                <TableCell className={classes.headCell}>Message Type</TableCell>
                <TableCell className={classes.headCell}>Change Type</TableCell>
                <TableCell className={classes.headCell}>Version (Mosymphony)</TableCell>
                <TableCell className={classes.headCell}>Created At</TableCell>
                <TableCell className={classes.headCell} align='center'>What Changed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.length === 0 && (
                <TableRow>
                  <TableCell className={classes.bodyCell} colSpan={7} align='center'>
                    No IDocs found for the selected date range.
                  </TableCell>
                </TableRow>
              )}
              {pageRows.map((row, i) => (
                <TableRow key={`${row.poNumber}-${row.createdAt}-${i}`} hover>
                  <TableCell className={classes.bodyCell}>{row.poNumber}</TableCell>
                  <TableCell className={classes.bodyCell}>{row.cmoName}</TableCell>
                  <TableCell className={classes.bodyCell}>{row.messageType}</TableCell>
                  <TableCell className={classes.bodyCell}>
                    <span className={this.badgeClass(row.changeType)}>{row.changeType}</span>
                  </TableCell>
                  <TableCell className={classes.bodyCell}>{row.version}</TableCell>
                  <TableCell className={classes.bodyCell}>{toLocalTime(row.createdAt)}</TableCell>
                  <TableCell className={classes.bodyCell} align='center'>
                    <IconButton
                      size='small'
                      className={classes.eyeBtn}
                      onClick={() => this.setState({ modalRow: row })}
                    >
                      <VisibilityOutlinedIcon fontSize='small' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <div className={classes.pagination}>
          <button
            className={classes.pageBtn}
            disabled={page === 0}
            onClick={() => this.setState({ page: page - 1 })}
          >
            &#8249;
          </button>
          <span>{page + 1} out of {pageCount} pages</span>
          <button
            className={classes.pageBtn}
            disabled={page >= pageCount - 1}
            onClick={() => this.setState({ page: page + 1 })}
          >
            &#8250;
          </button>
        </div>

        {this.renderModal()}
      </div>
    );
  }
}

export default withStyles(styles)(IdocReport);
